import { useEffect, useState } from "react";
import { ShoppingBag, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

interface LiveNotificationsPreviewProps {
  settings: {
    enabled?: boolean;
    showInterval?: number;
    displayDuration?: number;
    position?: string;
    backgroundColor?: string;
    textColor?: string;
    borderColor?: string;
    showAvatar?: boolean;
    notifications?: Array<{
      name: string;
      location: string;
      product: string;
      time: string;
    }>;
  };
}

export const LiveNotificationsPreview = ({ settings }: LiveNotificationsPreviewProps) => {
  const [currentNotification, setCurrentNotification] = useState<any>(null);
  const [notificationIndex, setNotificationIndex] = useState(0);

  const defaultNotifications = [
    { name: "أحمد محمد", location: "القاهرة", product: "كورسيه البطن", time: "منذ 5 دقائق" },
    { name: "فاطمة علي", location: "الإسكندرية", product: "مشد الخصر", time: "منذ 8 دقائق" },
    { name: "محمود حسن", location: "الجيزة", product: "باقة 2+1", time: "منذ 12 دقيقة" },
    { name: "سارة أحمد", location: "طنطا", product: "العرض الخاص", time: "منذ 15 دقيقة" },
    { name: "خالد يوسف", location: "المنصورة", product: "مجموعة التخسيس", time: "منذ 20 دقيقة" },
  ];

  const notifications = settings.notifications || defaultNotifications;
  const showInterval = settings.showInterval || 15000;
  const displayDuration = settings.displayDuration || 8000;

  useEffect(() => {
    if (!settings.enabled) return;

    const showNotification = () => {
      setCurrentNotification(notifications[notificationIndex]);
      setNotificationIndex((prev) => (prev + 1) % notifications.length);

      setTimeout(() => {
        setCurrentNotification(null);
      }, displayDuration);
    };

    showNotification();
    const interval = setInterval(showNotification, showInterval);

    return () => clearInterval(interval);
  }, [settings.enabled, notificationIndex, notifications, showInterval, displayDuration]);

  const getPositionStyles = () => {
    const position = settings.position || "bottom-left";
    const styles: any = { position: "fixed", zIndex: 50 };

    switch (position) {
      case "bottom-left":
        return { ...styles, bottom: "20px", left: "20px" };
      case "bottom-right":
        return { ...styles, bottom: "20px", right: "20px" };
      case "top-left":
        return { ...styles, top: "20px", left: "20px" };
      case "top-right":
        return { ...styles, top: "20px", right: "20px" };
      default:
        return { ...styles, bottom: "20px", left: "20px" };
    }
  };

  if (!settings.enabled) return null;

  return (
    <div style={getPositionStyles()}>
      <AnimatePresence>
        {currentNotification && (
          <motion.div
            initial={{ opacity: 0, y: 50, scale: 0.8 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            className="shadow-lg rounded-lg p-4 max-w-sm"
            style={{
              backgroundColor: settings.backgroundColor || "#ffffff",
              borderLeft: `4px solid ${settings.borderColor || "#10b981"}`,
              color: settings.textColor || "#000000",
            }}
          >
            <div className="flex items-start gap-3">
              {settings.showAvatar && (
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: settings.borderColor || "#10b981" }}
                >
                  <ShoppingBag className="w-5 h-5 text-white" />
                </div>
              )}
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1">
                  <p className="font-bold text-sm">{currentNotification.name}</p>
                  <button
                    onClick={() => setCurrentNotification(null)}
                    className="opacity-50 hover:opacity-100"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <p className="text-xs opacity-75 mb-1">
                  من {currentNotification.location}
                </p>
                <p className="text-sm">اشترى {currentNotification.product}</p>
                <p className="text-xs opacity-60 mt-1">{currentNotification.time}</p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
